import React, { useContext, useState } from 'react'
import dynamic from 'next/dynamic'

import type { Board } from '../Collection/RoomsCollection'
import RoomsCollection from '../Collection/RoomsCollection'

import styles from './styles.module.css'
import { RoomConfigurator, RoomPreview } from '@s4l/uikit'
import ShowMore from '../RoomResponsive/ShowMore'
import { useS4LContext } from '@s4l/react-context'
import { ROOM_CONFIGURATOR_EXPERIMENT } from 'src/constants/experimentConfig'
import useExperiment from 'src/hooks/useExperimentConfig'
import { RoomsContext } from '../context'
import { GridLayout } from '@s4l/ds'
import { CustomMediaBreakpoints } from '@s4l/utils/hooks/useBrowser'
import type { Room } from '@s4l/stayforlong/booking/Room'
import { cn } from '@s4l/utility-css'
import { ListOfAvailability } from '@s4l/stayforlong/booking/ListOfAvailability'
import { type AffiliateType } from '@s4l/stayforlong/shared/domain/Affiliate'
import { type Markets } from '@s4l/stayforlong/shared/domain/Market'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { PAGES, SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { useRouter } from 'next/router'

const Header = dynamic(() => import('../RoomResponsive/AvailabilityHeader'))
const RoomDetailsModal = dynamic(() => import('../RoomResponsive/RoomDetailsContainer'))
const RoomDetails = dynamic(() => import('../RoomResponsive/RoomDetails'))

const AVAILABILITY_ROOMS_PER_PAGE = 3

interface IRoomsAvailability {
  defaultOpen?: boolean
  showHeader?: boolean
  rooms: Board[]
  market: string
  affiliate: string
  checkIn: string
  checkOut: string
}

const RoomsAvailability = ({
  defaultOpen = true,
  showHeader = false,
  rooms = [],
  market,
  affiliate,
  checkIn,
  checkOut,
}: IRoomsAvailability) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const { i18n } = useS4LContext()
  const [showDetails, setShowDetails] = useState<Room | null>(null)

  const router = useRouter()

  const { sendEvent: sendRoomEvent } = useKafkaEvent({
    eventName: 'click',
    page: PAGES.DETAIL,
  })

  const {
    isMultiRoom,
    handleSelectRoom,
    selectedRooms,
    isBookingRoom,
    occupancies,
    locationState,
  } = useContext(RoomsContext)

  const { variant } = useExperiment(ROOM_CONFIGURATOR_EXPERIMENT.ID)
  const isRoomConfiguratorFF = router.query.ffRoomConfigurator === 'true'
  const isRoomConfiguratorExp =
    variant === ROOM_CONFIGURATOR_EXPERIMENT.NEW_DESIGN || isRoomConfiguratorFF

  const [availabilityPage, setAvailabilityPage] = useState(0)

  const { adults, children, roomsNumber, childrenAges, beds } = rooms[0]

  const maxVisibleElements =
    availabilityPage * AVAILABILITY_ROOMS_PER_PAGE + AVAILABILITY_ROOMS_PER_PAGE

  const renderRoomDetailModal = () => {
    if (!showDetails) return null

    const currentBoard = rooms.find(board => board.roomUniqueId === showDetails.roomID().value)

    return (
      <RoomDetailsModal isOpen handleClose={() => setShowDetails(null)}>
        <RoomDetails
          capacity={showDetails.adults() + showDetails.children()}
          facilities={currentBoard?.facilities ?? []}
          photos={currentBoard?.photos ?? []}
          title={showDetails.name()}
          modalCallback={() => setShowDetails(null)}
        />
      </RoomDetailsModal>
    )
  }

  const handleClickRoom = (firstAvailabilityRoom: Room) => {
    setShowDetails(firstAvailabilityRoom)
    sendRoomEvent(
      { section: SECTIONS.ROOM_IMAGES },
      {
        occupancies,
        search: {
          country: locationState?.country,
          type: locationState?.citySlug ? 'city' : 'zone',
          slug: locationState?.slug,
        },
      }
    )
  }

  const listOfAvailability = isRoomConfiguratorExp
    ? ListOfAvailability.fromReduxBoardsOrNull({
        boards: rooms as any,
        checkin: checkIn,
        checkout: checkOut,
        market: market as Markets,
        affiliate: affiliate as AffiliateType,
      })
    : ListOfAvailability.empty()

  const availabilityRoomsId = listOfAvailability?.getRooms() ?? []

  return (
    <div className={styles.roomAvailability}>
      {showHeader && (
        <Header
          adults={adults}
          // eslint-disable-next-line react/no-children-prop
          children={children} // This "children" refers to paxes and not to component children
          roomsNumber={roomsNumber}
          childrenAges={childrenAges}
          beds={beds}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      )}

      {isRoomConfiguratorExp ? (
        <div className={cn(styles.roomConfiguratorGrid, !isOpen ? styles.hidden : '')}>
          <GridLayout
            columns={[
              { breakpoint: CustomMediaBreakpoints.Mobile, column: 1 },
              { breakpoint: CustomMediaBreakpoints.MobileL, column: 1 },
              { breakpoint: CustomMediaBreakpoints.Tablet, column: 2 },
              { breakpoint: CustomMediaBreakpoints.Desktop, column: 3 },
            ]}
          >
            {listOfAvailability &&
              availabilityRoomsId?.slice(0, maxVisibleElements)?.map(roomID => {
                const roomAvailability = listOfAvailability.getListOfAvailabilityByRoomID({
                  id: roomID,
                })

                const firstAvailabilityRoom = roomAvailability.at(0)?.room()

                return (
                  <RoomConfigurator
                    key={roomID.value}
                    roomID={roomID}
                    listOfAvailability={roomAvailability}
                    isMultiBooking={isMultiRoom}
                  >
                    {firstAvailabilityRoom ? (
                      <RoomConfigurator.Header>
                        <RoomPreview
                          room={firstAvailabilityRoom}
                          onClickSeeRoom={() => handleClickRoom(firstAvailabilityRoom)}
                        />
                      </RoomConfigurator.Header>
                    ) : null}
                    <RoomConfigurator.Form
                      loading={isBookingRoom}
                      onSelectAvailability={availability => {
                        const currentBoard = rooms.find(
                          board => board.availabilityId === availability.id()
                        )

                        if (!currentBoard) return

                        const isBoardSelected = selectedRooms.some(
                          (selectedRoom: Board) =>
                            selectedRoom.availabilityId === currentBoard?.availabilityId
                        )

                        handleSelectRoom({
                          board: currentBoard,
                          isRemove: isBoardSelected,
                          closeGroup: () => setIsOpen(false),
                        })
                      }}
                      onChange={data => {
                        const { kind, availability } = data
                        const currentBoard = rooms.find(
                          board => board.availabilityId === availability.id()
                        )
                        const section = kind === 'board' ? SECTIONS.ROOM_BOARD : SECTIONS.ROOM_RATE
                        sendRoomEvent(
                          { section },
                          {
                            availabilityId: currentBoard?.availabilityId,
                            rate_class: currentBoard?.paymentPolicy,
                            room_images: currentBoard?.photos.length,
                            cancellationPolicy: currentBoard?.cancellation,
                            board: currentBoard?.code,
                          }
                        )
                      }}
                    />
                  </RoomConfigurator>
                )
              })}
          </GridLayout>
          {availabilityRoomsId && availabilityRoomsId.length > maxVisibleElements ? (
            <div className={styles.showMoreContainer}>
              <ShowMore onClick={() => setAvailabilityPage(availabilityPage + 1)}>
                {i18n.formatMessage(
                  { id: 'global.show-more-rooms-from' },
                  { rooms: availabilityRoomsId.length - maxVisibleElements }
                )}
              </ShowMore>
            </div>
          ) : null}
          {renderRoomDetailModal()}
        </div>
      ) : (
        <RoomsCollection
          isOpen={isOpen}
          closeGroup={() => setIsOpen(false)}
          rooms={rooms}
          withHeader={showHeader}
        />
      )}
    </div>
  )
}

export default RoomsAvailability
